/**
 * BST Drag Scroll — click-and-drag horizontal scrolling for overflow tracks.
 */
const BST_DRAG_SCROLL_TRACK = '[data-bst-drag-scroll-track]';
const BST_DRAG_SCROLL_THRESHOLD = 5;

class BstDragScroll extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.isPointerDown = false;
    this.isDragging = false;
    this.didDrag = false;
    this.pointerId = null;
    this.startX = 0;
    this.startScrollLeft = 0;

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector(BST_DRAG_SCROLL_TRACK) || this.firstElementChild;
    if (!this.track) return;

    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('click', this.onClickCapture, true);
  }

  disconnectedCallback() {
    if (!this.track) return;

    this.track.removeEventListener('pointerdown', this.onPointerDown);
    this.track.removeEventListener('click', this.onClickCapture, true);
    this.endDrag();
  }

  isScrollable() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!this.isScrollable()) return;
    if (event.target.closest('button, input, select, textarea, label')) return;

    this.isPointerDown = true;
    this.isDragging = false;
    this.didDrag = false;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScrollLeft = this.track.scrollLeft;

    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
  }

  onPointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

    const deltaX = event.clientX - this.startX;
    if (!this.isDragging && Math.abs(deltaX) <= BST_DRAG_SCROLL_THRESHOLD) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.didDrag = true;
      this.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.track.scrollLeft = this.startScrollLeft - deltaX;
  }

  onPointerUp(event) {
    if (!this.isPointerDown || event.pointerId !== this.pointerId) return;
    this.endDrag();
  }

  endDrag() {
    if (!this.track) return;

    const hadDrag = this.didDrag;

    this.isPointerDown = false;
    this.isDragging = false;
    this.classList.remove('is-dragging');

    if (this.pointerId !== null) {
      try {
        this.track.releasePointerCapture(this.pointerId);
      } catch (error) {
        // Pointer may already be released.
      }
    }

    this.pointerId = null;
    this.track.removeEventListener('pointermove', this.onPointerMove);
    this.track.removeEventListener('pointerup', this.onPointerUp);
    this.track.removeEventListener('pointercancel', this.onPointerUp);

    if (hadDrag) {
      window.setTimeout(() => {
        this.didDrag = false;
      }, 0);
    } else {
      this.didDrag = false;
    }
  }

  onClickCapture(event) {
    if (!this.didDrag) return;
    event.preventDefault();
    event.stopPropagation();
  }
}

if (!customElements.get('bst-drag-scroll')) {
  customElements.define('bst-drag-scroll', BstDragScroll);
}
